import {type Dispatch, type SetStateAction, useLayoutEffect} from 'react';

import {VehicleMotWarningResponseBody} from '@dms/api/core';

import {useAdaptiveElementSize} from '../../../hooks/useAdaptiveElementSize';
import {VehicleMotCategory} from '../../../utils/getVehicleMotDataQuery';
import {EMPTY_SECTION_HEIGHTS} from '../constants/emptySectionHeights';
import {VehicleMotSectionMeasurements} from '../types/VehicleMotSectionMeasurements';
import {VehicleWarehouseAdaptiveMotProps} from '../types/VehicleWarehouseAdaptiveMotProps';

interface VehicleMotSectionProbeProps {
  category: VehicleMotCategory;
  rowCount: number;
  count: number | undefined;
  rows: VehicleMotWarningResponseBody[];
  resetKey: string;
  renderSection: VehicleWarehouseAdaptiveMotProps['renderSection'];
  setMeasurements: Dispatch<SetStateAction<VehicleMotSectionMeasurements>>;
}

export function VehicleMotSectionProbe(props: VehicleMotSectionProbeProps) {
  const [ref, width, height] = useAdaptiveElementSize<HTMLDivElement>({resetKey: props.resetKey});

  // Reports before paint so the parent picks its MOT layout from this measurement without first
  // painting a stale one.
  useLayoutEffect(
    function reportVehicleMotSectionHeight() {
      if (width === null || height === null) {
        return;
      }

      props.setMeasurements((current) => {
        const isCurrentGeneration =
          current.contentKey === props.resetKey && current.width === width;
        const values = isCurrentGeneration ? current.values : [...EMPTY_SECTION_HEIGHTS];
        if (isCurrentGeneration && values[props.rowCount] === height) {
          return current;
        }

        return {
          contentKey: props.resetKey,
          width,
          values: values.map((measurement, index) =>
            index === props.rowCount ? height : measurement
          ),
        };
      });
    },
    [height, props.resetKey, props.rowCount, props.setMeasurements, width] // eslint-disable-line react/exhaustive-deps -- Muted pre-existing violation
  );

  return (
    <div
      ref={ref}
      data-vehicle-warehouse-probe-category={props.category}
      data-vehicle-warehouse-probe-rows={props.rowCount}
    >
      {props.renderSection(props.category, props.count, props.rows, props.rowCount, true)}
    </div>
  );
}
